import { Injectable, Optional, Inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserPreferencesService } from './user-preferences.service';

export type ReasoningLevel = 'auto' | 'instant' | 'thinking' | 'pro';

export interface ReasoningModelConfig {
  modelId: string;
  supportsReasoning: boolean;
  reasoningType?: 'o3' | 'gpt5-mini' | 'gemini' | 'grok' | 'glm';
  reasoningMapping?: {
    auto?: string;
    instant?: string;
    thinking?: string;
    pro?: string;
  };
}

@Injectable({ providedIn: 'root' })
export class ReasoningService {
  private readonly reasoningLevelSubject = new BehaviorSubject<ReasoningLevel>('thinking');
  readonly reasoningLevel$ = this.reasoningLevelSubject.asObservable();
  private preferencesService?: UserPreferencesService;

  // Models that support reasoning parameters
  private readonly reasoningModels: Map<string, ReasoningModelConfig> = new Map([
    // o3 model - supports effort: 'low' | 'medium' | 'deep'
    ['o3', {
      modelId: 'o3',
      supportsReasoning: true,
      reasoningType: 'o3',
      reasoningMapping: {
        auto: 'low',
        instant: 'low',
        thinking: 'medium',
        pro: 'deep'
      }
    }],
    // gpt-5-mini - supports reasoningLevel: 'low' | 'medium' | 'high'
    ['gpt5mini', {
      modelId: 'gpt5mini',
      supportsReasoning: true,
      reasoningType: 'gpt5-mini',
      reasoningMapping: {
        auto: 'low',
        instant: 'low',
        thinking: 'medium',
        pro: 'high'
      }
    }],
    ['chatgpt5-mini', {
      modelId: 'chatgpt5-mini',
      supportsReasoning: true,
      reasoningType: 'gpt5-mini',
      reasoningMapping: {
        auto: 'low',
        instant: 'low',
        thinking: 'medium',
        pro: 'high'
      }
    }],
    // chatgpt5 - may support reasoning
    ['chatgpt5', {
      modelId: 'chatgpt5',
      supportsReasoning: true,
      reasoningType: 'gpt5-mini',
      reasoningMapping: {
        auto: 'low',
        instant: 'low',
        thinking: 'medium',
        pro: 'high'
      }
    }],
    // Gemini - supports thinkingLevel: 'MINIMAL' | 'LOW' | 'MEDIUM' | 'HIGH'
    ['gemini', {
      modelId: 'gemini',
      supportsReasoning: true,
      reasoningType: 'gemini',
      reasoningMapping: {
        auto: 'MINIMAL',
        instant: 'MINIMAL',
        thinking: 'MEDIUM',
        pro: 'HIGH'
      }
    }],
    ['gemini3flash', {
      modelId: 'gemini3flash',
      supportsReasoning: true,
      reasoningType: 'gemini',
      reasoningMapping: {
        auto: 'MINIMAL',
        instant: 'MINIMAL',
        thinking: 'MEDIUM',
        pro: 'HIGH'
      }
    }],
    // GLM 4.7 (zai)
    ['zai', {
      modelId: 'zai',
      supportsReasoning: true,
      reasoningType: 'glm',
      reasoningMapping: {
        auto: 'disabled',
        instant: 'disabled',
        thinking: 'enabled',
        pro: 'enabled'
      }
    }],
    // OpenAI models - ChatGPT variants
    ['chatgpt', {
      modelId: 'chatgpt',
      supportsReasoning: true,
      reasoningType: 'gpt5-mini',
      reasoningMapping: {
        auto: 'low',
        instant: 'low',
        thinking: 'medium',
        pro: 'high'
      }
    }],
    ['chatgpt41', {
      modelId: 'chatgpt41',
      supportsReasoning: true,
      reasoningType: 'gpt5-mini',
      reasoningMapping: {
        auto: 'low',
        instant: 'low',
        thinking: 'medium',
        pro: 'high'
      }
    }],
    ['chatgpt52', {
      modelId: 'chatgpt52',
      supportsReasoning: true,
      reasoningType: 'gpt5-mini',
      reasoningMapping: {
        auto: 'low',
        instant: 'low',
        thinking: 'medium',
        pro: 'high'
      }
    }],
    // Grok models
    ['grok', {
      modelId: 'grok',
      supportsReasoning: true,
      reasoningType: 'grok',
      reasoningMapping: {
        auto: 'low',
        instant: 'low',
        thinking: 'medium',
        pro: 'high'
      }
    }],
    ['grok41', {
      modelId: 'grok41',
      supportsReasoning: true,
      reasoningType: 'grok',
      reasoningMapping: {
        auto: 'low',
        instant: 'low',
        thinking: 'medium',
        pro: 'high'
      }
    }],
    ['grokimage', {
      modelId: 'grokimage',
      supportsReasoning: true,
      reasoningType: 'grok',
      reasoningMapping: {
        auto: 'low',
        instant: 'low',
        thinking: 'medium',
        pro: 'high'
      }
    }]
  ]);

  constructor(@Optional() @Inject(UserPreferencesService) preferencesService?: UserPreferencesService) {
    this.preferencesService = preferencesService;
    // Load saved reasoning level from localStorage (fallback)
    const savedLevel = localStorage.getItem('selectedReasoningLevel');
    if (savedLevel && ['auto', 'instant', 'thinking', 'pro'].includes(savedLevel)) {
      this.reasoningLevelSubject.next(savedLevel as ReasoningLevel);
    }
  }

  /**
   * Get current reasoning level
   */
  getReasoningLevel(): ReasoningLevel {
    return this.reasoningLevelSubject.value;
  }

  /**
   * Set reasoning level
   * @param level The reasoning level to set
   * @param modelId Optional model ID to save per-model preference
   */
  setReasoningLevel(level: ReasoningLevel, modelId?: string): void {
    this.reasoningLevelSubject.next(level);
    localStorage.setItem('selectedReasoningLevel', level);
    
    // If modelId is provided and preferences service is available, save per-model preference
    if (modelId && this.preferencesService) {
      this.preferencesService.saveReasoningLevelForModel(modelId, level).catch(err => {
        console.error('Error saving reasoning level preference:', err);
      });
    }
  }

  /**
   * Get reasoning level for a specific model (from Firebase preferences)
   */
  async getReasoningLevelForModel(modelId: string): Promise<ReasoningLevel> {
    if (this.preferencesService) {
      const savedLevel = await this.preferencesService.getReasoningLevelForModel(modelId);
      if (savedLevel) {
        return savedLevel;
      }
    }
    // Fallback to global reasoning level
    return this.getReasoningLevel();
  }

  /**
   * Check if a model supports reasoning
   */
  modelSupportsReasoning(modelId: string): boolean {
    return this.reasoningModels.has(modelId) && 
           this.reasoningModels.get(modelId)!.supportsReasoning;
  }

  /**
   * Get reasoning config for a model
   */
  getModelReasoningConfig(modelId: string): ReasoningModelConfig | null {
    return this.reasoningModels.get(modelId) || null;
  }

  /**
   * Get the mapped reasoning value for a specific model
   */
  getMappedReasoningValue(modelId: string, level?: ReasoningLevel): string | null {
    const config = this.getModelReasoningConfig(modelId);
    if (!config || !config.reasoningMapping) return null;

    const targetLevel = level || this.getReasoningLevel();
    return config.reasoningMapping[targetLevel] || null;
  }

  /**
   * Get all models that support reasoning
   */
  getReasoningCapableModels(): string[] {
    return Array.from(this.reasoningModels.keys());
  }

  /**
   * Convert reasoning level to o3 effort format
   */
  toO3Effort(level?: ReasoningLevel): 'low' | 'medium' | 'deep' {
    const targetLevel = level || this.getReasoningLevel();
    const mapping: Record<ReasoningLevel, 'low' | 'medium' | 'deep'> = {
      auto: 'low',
      instant: 'low',
      thinking: 'medium',
      pro: 'deep'
    };
    return mapping[targetLevel];
  }

  /**
   * Convert reasoning level to gpt5-mini format
   */
  toGpt5MiniReasoning(level?: ReasoningLevel): 'low' | 'medium' | 'high' {
    const targetLevel = level || this.getReasoningLevel();
    const mapping: Record<ReasoningLevel, 'low' | 'medium' | 'high'> = {
      auto: 'low',
      instant: 'low',
      thinking: 'medium',
      pro: 'high'
    };
    return mapping[targetLevel];
  }

  /**
   * Convert reasoning level to Gemini thinking level format
   */
  toGeminiThinkingLevel(level?: ReasoningLevel): 'MINIMAL' | 'LOW' | 'MEDIUM' | 'HIGH' {
    const targetLevel = level || this.getReasoningLevel();
    const mapping: Record<ReasoningLevel, 'MINIMAL' | 'LOW' | 'MEDIUM' | 'HIGH'> = {
      auto: 'MINIMAL',
      instant: 'MINIMAL',
      thinking: 'MEDIUM',
      pro: 'HIGH'
    };
    return mapping[targetLevel];
  }

  /**
   * Convert reasoning level to ZAI (GLM) thinking format
   */
  toZaiThinkingLevel(level?: ReasoningLevel): 'enabled' | 'disabled' {
    const targetLevel = level || this.getReasoningLevel();
    const mapping: Record<ReasoningLevel, 'enabled' | 'disabled'> = {
      auto: 'disabled',
      instant: 'disabled',
      thinking: 'enabled',
      pro: 'enabled'
    };
    return mapping[targetLevel];
  }
}
